import { useCallback, useContext, useEffect, useState } from "react";
import { saveDocumentInfo } from "../../../libraries/api";
import { normaliseDocument, uploadDocument } from "./utils";
import Context from "../../../context";

export const createInitialUploadDocConfig = () => ({
  docData: { id: null, name: "" },
  restrictedToTravellerIds: null
});
export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MiB
export const toMB = (size, decimals = 2) => (size / 1_000_000).toFixed(decimals);
export const toMiB = (size, decimals = 2) => (size / 1024 / 1024).toFixed(decimals);

export const useDocumentUpload = ({ folderId, fileInputRef, cancelUploadSignal, setCancelUploadSignal, setIsUploadOpen}) => {
  const [myDocsUploadDetails, setMyDocsUploadDetails] = useState({
    uploading: false,
    totalSize: 0,
    uploadedBytes: 0,
    percent: 0
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadDocConfig, setUploadDocConfig] = useState(createInitialUploadDocConfig());
  const [isRetry, setIsRetry] = useState(false);
  const [cachedUploadData, setCachedUploadData] = useState(null);
  const [isConverting, setIsConverting] = useState(false);

  const { itin, setGlobalModalContent, setItin, t } = useContext(Context);

  const maxUploadSize = itin?.maxUploadSize || MAX_FILE_SIZE;

  const onFileSelect = async (e) => {
    const files = Array.from(e.target.files);

    const file = await normaliseDocument(files[0], setIsConverting);
    if (!file) {
      setGlobalModalContent(() => ({
        closable: true,
        message: t("error_image_conversion_web"),
        buttons: [{ text: t('ok'), type: 'close' }]
      }));

      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (file?.size > maxUploadSize) {
      const fileSizeMb = toMB(file?.size, 1);
      const maxSizeMb = toMiB(maxUploadSize, 1);

      setGlobalModalContent(() => ({
        closable: true,
        header: t("document_file_too_large_title", { lng: itin.language }),
        message: t("document_file_too_large_message", { lng: itin.language })
          .replace('%1$.2f', fileSizeMb)
          .replace('%2$.2f', maxSizeMb),
        buttons: [{ text: t('ok'), type: 'close' }]
      }));

      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const fileNameWithoutExt = file?.name?.replace(/\.[^/.]+$/, "") ?? "";
    setUploadDocConfig(prevState => ({
      ...prevState,
      docData: {
        ...(prevState?.docData ?? {}),
        name: fileNameWithoutExt,
      },
    }));
    setSelectedFile(file);
    setIsUploadOpen(true);
  };

  const onCancel = useCallback(() => {
    setIsUploadOpen(false);
    setIsRetry(false);
    setSelectedFile(null);
    setUploadDocConfig(createInitialUploadDocConfig());
    setCachedUploadData(null);

    if (fileInputRef?.current) {
      fileInputRef.current.value = "";
    }
    setCancelUploadSignal(false);
  }, [fileInputRef, setCancelUploadSignal, setIsUploadOpen]);

  const handleFileUpload = async () => {
    try {
      setMyDocsUploadDetails({
        uploading: true,
        totalSize: selectedFile.size,
        uploadedBytes: 0,
        percent: 0,
      });

      const travellerId = itin?.clients?.find(client => client.isCurrent)?.id;
      let uploadedDocData;

      if (cachedUploadData?.s3url && uploadDocConfig.docData.name === cachedUploadData?.fileName) {
        // Retry only saveDocumentInfo with cached S3 URL
        const saveDocumentInfoBody = {
          folderId,
          fileUrl: cachedUploadData?.s3url,
          restrictedToTravellerIds: uploadDocConfig.restrictedToTravellerIds,
          name: cachedUploadData?.fileName,
        }

        const {
          res,
          data,
          error
        } = await saveDocumentInfo(itin.operatorCode, itin.passcode, saveDocumentInfoBody, travellerId);
        if (!res.ok || error) {
          const msg = error || data?.error?.[0]?.message || data?.error || "Save document info error";
          throw new Error(msg);
        }

        uploadedDocData = data;
      } else {
        uploadedDocData = await uploadDocument(
          selectedFile,
          uploadDocConfig.docData.name,
          itin,
          folderId,
          travellerId,
          uploadDocConfig.restrictedToTravellerIds,
          setCachedUploadData,
          (percent, loaded) => {
            setMyDocsUploadDetails(prev => ({
              ...prev,
              uploadedBytes: loaded,
              percent
            }));
          }
        );
      }

      setMyDocsUploadDetails(prev => ({
        ...prev,
        percent: 100,
      }));

      setItin(prevItin => {
        const updatedDocs = prevItin.documents.map(doc => {
          if (doc.id === folderId) {
            return {
              ...doc,
              entries: [...doc.entries, uploadedDocData.id]
            };
          }
          return doc;
        });

        const finalDocs = [...updatedDocs, uploadedDocData];

        return {
          ...prevItin,
          documents: finalDocs
        };
      });

      onCancel();
    } catch (err) {
      const internalErrMsg =  err || err?.message || "Unexpected upload error";
      console.error('Upload error:', internalErrMsg)

      setGlobalModalContent(() => ({
        closable: true,
        message: `${t("document_failed_to_upload")} ${uploadDocConfig.docData.name}`,
        buttons: [{ text: t('ok'), type: 'close' }]
      }));
      setIsRetry(true)
    } finally {
      setMyDocsUploadDetails({
        uploading: false,
        totalSize: 0,
        uploadedBytes: 0,
        percent: 0,
      });
    }
  };

  useEffect(() => {
    if (!cancelUploadSignal) return;

    onCancel();
  }, [onCancel, cancelUploadSignal]);

  return {
    isRetry,
    uploadDocConfig,
    setUploadDocConfig,
    myDocsUploadDetails,
    setMyDocsUploadDetails,
    onFileSelect,
    onCancel,
    handleFileUpload,
    isConverting,
  };
};